import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ShoppingCart, Mail, RefreshCw, CheckCircle2, Clock, Send } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { adminAPI } from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const fmtCOP = (v) => '$' + String(Math.round(v || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">{label}</p>
    <p className="font-display font-bold text-3xl text-[#1C1917] mt-1">{value}</p>
  </div>
);

const StatusBadge = ({ cart }) => {
  if (cart.completed_at) {
    return (
      <span className="text-[10px] bg-[#F0F5EF] text-[#2A5C43] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest inline-flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> Recuperado
      </span>
    );
  }
  if (cart.notified_at) {
    return (
      <span className="text-[10px] bg-[#FFF7EE] text-[#D47A4A] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest inline-flex items-center gap-1">
        <Send className="w-3 h-3" /> Notificado
      </span>
    );
  }
  return (
    <span className="text-[10px] bg-[#F5F5F4] text-[#57534E] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest inline-flex items-center gap-1">
      <Clock className="w-3 h-3" /> Abierto
    </span>
  );
};

const AdminAbandonedCarts = () => {
  const [data, setData] = useState({ carts: [], stats: { open: 0, notified: 0, recovered: 0 } });
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listAbandonedCarts();
      setData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const res = await adminAPI.forceSweepCarts();
      toast.success(`Barrido completado — ${res.sent} recordatorio${res.sent === 1 ? '' : 's'} enviado${res.sent === 1 ? '' : 's'}`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar barrido');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-carts-title">
              Carritos abandonados
            </h1>
            <p className="text-[#57534E] mt-2 max-w-xl">
              Un barrido automático corre cada 10 minutos: cualquier carrito con &gt; 2 horas sin actividad recibe un email recordatorio (sin cupón). Puedes forzar un barrido manual.
            </p>
          </div>
          <Button
            onClick={runSweep}
            disabled={sweeping}
            className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full"
            data-testid="admin-carts-sweep"
          >
            {sweeping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Ejecutar barrido ahora
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={ShoppingCart} label="Abiertos" value={data.stats.open} tint="bg-[#F5F5F4] text-[#57534E]" />
          <StatCard icon={Mail} label="Notificados sin comprar" value={data.stats.notified} tint="bg-[#FFF7EE] text-[#D47A4A]" />
          <StatCard icon={CheckCircle2} label="Recuperados" value={data.stats.recovered} tint="bg-[#F0F5EF] text-[#2A5C43]" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
        ) : data.carts.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E7E5E4] p-16 text-center" data-testid="admin-carts-empty">
            <ShoppingCart className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
            <p className="text-[#57534E]">Aún no hay carritos abandonados registrados.</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden" data-testid="admin-carts-list">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAF9F5] text-[10px] uppercase tracking-widest text-[#8BA58F]">
                  <tr>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Items</th>
                    <th className="text-right px-4 py-3">Total</th>
                    <th className="text-left px-4 py-3">Última acción</th>
                    <th className="text-left px-4 py-3">Notificado</th>
                    <th className="text-left px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.carts.map((c) => (
                    <tr key={c.id} className="border-t border-[#F5F5F4]" data-testid={`cart-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#1C1917]">{c.customer_name || '—'}</p>
                        <p className="text-xs text-[#8BA58F]">{c.email}</p>
                      </td>
                      <td className="px-4 py-3 max-w-sm">
                        <ul className="text-xs text-[#57534E] space-y-0.5">
                          {(c.items || []).slice(0, 3).map((it, i) => (
                            <li key={i} className="truncate">
                              {it.quantity}× {it.product_name} <span className="text-[#8BA58F]">{it.size || ''}</span>
                            </li>
                          ))}
                          {(c.items || []).length > 3 && (
                            <li className="text-[#8BA58F]">+ {(c.items || []).length - 3} más…</li>
                          )}
                        </ul>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-[#1C1917]">{fmtCOP(c.subtotal)}</td>
                      <td className="px-4 py-3 text-xs text-[#57534E]">{fmtDate(c.updated_at)}</td>
                      <td className="px-4 py-3 text-xs text-[#57534E]">{fmtDate(c.notified_at)}</td>
                      <td className="px-4 py-3"><StatusBadge cart={c} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAbandonedCarts;
